import { createSlice, current } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import {
  isFailedJob,
  isIncompleteJob,
} from '@sonifi/wb-core/services/jobsHelper';
import {
  getPublishStatusMessage,
  getHasUnpublishedChanges,
  findNodesByPlatform,
  createVisibleChildren,
  getPagination,
  isBeyondMaxChildren,
  isMaxNested,
} from './irdMenuSliceHelper';
import { PLATFORMS } from '@sonifi/wb-screenbuilder/constants/wbScreenBuilderConstants';
import { findNodeById, findNodeParent } from '@sonifi/wb-tree/utils/helper';
import {
  getBatchCollection,
  isAlwaysAvailable,
  newNodeTimeCheck,
} from './irdMenuFormsHelper';
import { GROUP_DELIVERY_METHOD } from '@sonifi/wb-group/constants/wbGroupConstants';
import { IRD_NODE_TYPES } from '@sonifi/wb-in-room-dining/constants/wbInRoomDiningConstants';

export const IRD_OPTIONS_TREE_KEYS = Object.freeze({
  MENU_BUILDER: 'menu_builder',
  ITEM_MODAL: 'item_modal',
  OPTION_SET_MODAL: 'option_set_modal',
});

const initialState = {
  actualPage: 0,
  addNodeFrom: null,
  background: null,
  canAddAdditional: false,
  categoryFormOptions: {
    alwaysAvailable: true,
    batchCollection: false,
    showOrderTimes: true,
    showPreOrder: true,
    valid_child_types: IRD_NODE_TYPES.ird_categories,
  },
  hasUnpublishedChanges: false,
  itemsDependencies: [],
  ird: null,
  irdMenu: null,
  isFailedJob: false,
  isIncompleteJob: false,
  isPublishing: false,
  itemForm: {},
  items: {
    [IRD_OPTIONS_TREE_KEYS.MENU_BUILDER]: {
      assignedOptionSets: [],
      items: [],
      loading: false,
      option: 'items',
      optionSets: [],
      page: 1,
      query: '',
      rpp: 30,
      total: 0,
      totalPages: 1,
    },
    [IRD_OPTIONS_TREE_KEYS.ITEM_MODAL]: {
      assignedOptionSets: [],
      loading: false,
      option: 'optionSets',
      optionSets: [],
      page: 1,
      query: '',
      rpp: 9,
      total: 0,
      totalPages: 1,
    },
    [IRD_OPTIONS_TREE_KEYS.OPTION_SET_MODAL]: {
      assignedItems: [],
      items: [],
      loading: false,
      option: 'items',
      page: 1,
      query: '',
      rpp: 9,
      total: 0,
      totalPages: 1,
    },
  },
  lastPublished: null,
  lastPublishUser: null,
  loadingForm: LOADING_STATUS.notLoading,
  loadingIrdMenu: LOADING_STATUS.notLoading,
  maxPage: null,
  menuItem: null,
  nodeMenuSelected: null,
  nodeParentSelected: null,
  nodePreviewParentSelected: null,
  nodePreviewSelected: null,
  nodeSelected: null,
  nodesPerPage: null,
  optionSetsDependencies: [],
  platform: PLATFORMS.tv,
  productSettings: null,
  publishStatusMessage: '',
  query: '',
  showModal: false,
  tvCategoryChildren: null,
  visibleChildren: [],
};

export const irdMenuSlice = createSlice({
  name: 'irdMenu',
  initialState,
  reducers: {
    setLoadingIrdMenu: (state, action) => {
      state.loadingIrdMenu = action.payload;
    },
    setMenu: (state, action) => {
      const {
        background,
        ird,
        lastPublished,
        lastPublishUser,
        latestPublishJob,
        platform,
        productSettings,
        irdMenu,
      } = action.payload;
      state.background = background;
      state.ird = ird;
      state.irdMenu = irdMenu;
      state.productSettings = productSettings;
      state.isIntegratedOrdering =
        productSettings.delivery_method === GROUP_DELIVERY_METHOD.integrated;
      state.isOrderByOptions =
        productSettings.delivery_method === GROUP_DELIVERY_METHOD.onSite &&
        productSettings.order_by_option;
      state.displayItemImages = productSettings.display_category_images;
      state.platform = platform;
      const incompleteJob = latestPublishJob
        ? isIncompleteJob(latestPublishJob)
        : null;
      const failedJob = latestPublishJob ? isFailedJob(latestPublishJob) : null;
      state.publishStatusMessage = getPublishStatusMessage({
        lastPublishJob: lastPublished,
        isIncompleteJob: incompleteJob,
        isFailedJob: failedJob,
      });
      state.isIncompleteJob = incompleteJob;
      state.isFailedJob = failedJob;
      state.lastPublished = lastPublished;
      state.lastPublishUser = lastPublishUser;
      const lastPublishedDate = lastPublished ? new Date(lastPublished) : null;
      state.hasUnpublishedChanges = getHasUnpublishedChanges({
        irdMenu,
        lastPublished: lastPublishedDate,
        productSettings,
      });
    },
    setIrdMenu(state, action) {
      const { irdMenu } = action.payload;
      state.irdMenu = irdMenu;
    },
    updateJobsStatus(state, action) {
      const { latestPublishJob, lastPublished, lastPublishUser } =
        action.payload;
      const { irdMenu, productSettings } = current(state);

      const incompleteJob = latestPublishJob
        ? isIncompleteJob(latestPublishJob)
        : null;
      const failedJob = latestPublishJob ? isFailedJob(latestPublishJob) : null;
      state.publishStatusMessage = getPublishStatusMessage({
        lastPublishJob: lastPublished,
        isIncompleteJob: incompleteJob,
        isFailedJob: failedJob,
      });
      state.isIncompleteJob = incompleteJob;
      state.isFailedJob = failedJob;
      state.lastPublished = lastPublished;
      state.lastPublishUser = lastPublishUser;
      const lastPublishedDate = lastPublished ? new Date(lastPublished) : null;
      state.hasUnpublishedChanges = getHasUnpublishedChanges({
        irdMenu,
        lastPublished: lastPublishedDate,
        productSettings,
      });
    },
    setNodePreviewSelected(state, action) {
      const { node } = action.payload;
      const { irdMenu, platform } = current(state);
      const { nodeParent, nodePreviewSelected } = findNodesByPlatform({
        menu: irdMenu.menu,
        node,
        platform,
      });

      const nPreviewSelected =
        node.type === IRD_NODE_TYPES.ird_items ? nodePreviewSelected : node;

      const pagination = getPagination({
        nodeSelected: nodePreviewSelected,
        platform,
      });

      const visibleChildren = createVisibleChildren({
        nodeSelected: nPreviewSelected.valid_child_types
          ? nPreviewSelected
          : nodeParent,
        actualPage: 0,
        nodesPerPage: pagination.nodesPerPage,
      });
      state.nodesPerPage = pagination.nodesPerPage;
      state.maxPage = pagination.maxPage;
      state.visibleChildren = visibleChildren;
      state.nodePreviewParentSelected = nodeParent;
      state.nodeMenuSelected = node;
      state.nodePreviewSelected = nPreviewSelected;
      state.actualPage = 0;
    },
    setPage(state, action) {
      const page = action.payload;
      const { nodePreviewSelected, nodesPerPage } = current(state);
      const visibleChildren = createVisibleChildren({
        actualPage: page,
        nodeSelected: nodePreviewSelected,
        nodesPerPage,
      });
      state.actualPage = page;
      state.visibleChildren = visibleChildren;
    },
    setNodeSelected(state, action) {
      const { node } = action.payload;
      const { irdMenu } = current(state);

      const nodeParentSelected = findNodeParent({
        menu: irdMenu.menu,
        node,
      });
      state.nodeParentSelected = nodeParentSelected;
      state.nodeSelected = node;
      if (node?.id) {
        state.canAddAdditional = false;
      } else {
        state.canAddAdditional = true;
      }
    },
    setIrdItems(state, action) {
      const {
        items,
        page,
        rpp,
        total,
        totalPages,
        query = '',
        orderBy,
        optionTreeKey,
      } = action.payload;

      const { items: itemsState } = current(state);
      state.items[optionTreeKey] = {
        assignedItems: itemsState[optionTreeKey].assignedItems,
        items,
        orderBy,
        page,
        query,
        rpp,
        total,
        totalPages,
        option: 'items',
      };
    },
    setIrdOptionSets(state, action) {
      const {
        itemsDependencies,
        optionSets,
        page,
        rpp,
        total,
        totalPages,
        query = '',
        orderBy,
        optionTreeKey,
      } = action.payload;
      const { items } = current(state);

      state.items[optionTreeKey] = {
        assignedOptionSets: items[optionTreeKey].assignedOptionSets,
        optionSets,
        option: 'optionSets',
        orderBy,
        page,
        query,
        rpp,
        total,
        totalPages,
      };
      state.itemsDependencies = itemsDependencies;
    },
    setNodeCategorySelected(state, action) {
      const { node } = action.payload;
      const { irdMenu } = current(state);

      const nodeParentSelected = findNodeParent({
        menu: irdMenu.menu,
        node,
      });

      const categoryFormOptions = {
        alwaysAvailable: isAlwaysAvailable(node),
        batchCollection: getBatchCollection(node),
        ...newNodeTimeCheck({
          menu: irdMenu.menu,
          nodeParent: nodeParentSelected,
          node,
        }),
        valid_child_types: node.valid_child_types[0],
      };
      state.canAddAdditional = false;
      state.nodeSelected = node;
      state.nodeParentSelected = nodeParentSelected;
      state.categoryFormOptions = categoryFormOptions;
    },
    setNodeCategorySelectedForAdd(state, action) {
      const { fromNodeId } = action.payload;
      const { irdMenu } = current(state);
      // Make sure have the latest Node state from the Tree
      const fromNode = findNodeById({ menu: irdMenu.menu, nodeId: fromNodeId });
      const nodeSelected = {
        type: fromNode.type,
        subject: {
          availability_end: '',
          availability_start: '',
          delivery_end: '',
          delivery_start: '',
          description: null,
          group: fromNode.group,
          title: null,
        },
        valid_child_types: [IRD_NODE_TYPES.ird_categories],
      };
      const parent = fromNode?.isRoot
        ? fromNode
        : findNodeParent({ menu: irdMenu.menu, node: fromNode });

      state.canAddAdditional = !isBeyondMaxChildren(
        fromNode?.children?.length + 1 || 0,
      ) && !isMaxNested(fromNode, 1);

      state.addNodeFrom = fromNode;
      state.nodeSelected = nodeSelected;
      state.nodeParentSelected = parent;
      state.categoryFormOptions = {
        ...initialState.categoryFormOptions,
      };
    },
    setNodeItemSelected(state, action) {
      const { node } = action.payload;
      const { irdMenu } = current(state);
      state.nodeParentSelected = findNodeParent({
        menu: irdMenu.menu,
        node,
      });
      state.canAddAdditional = false;
      state.nodeSelected = node;
    },
    setNodeItemSelectedForAdd(state, action) {
      const { fromNodeId } = action.payload;
      const { irdMenu } = current(state);
      // Make sure have the latest Node state from the Tree
      const fromNode = findNodeById({ menu: irdMenu.menu, nodeId: fromNodeId });

      const nodeSelected = {
        type: IRD_NODE_TYPES.ird_items,
        subject: {
          title: null,
          group: fromNode.subject.group,
          ird_option_sets: [],
          image: [],
          sku: null,
          description: null,
        },
      };
      const parent = fromNode?.isRoot
        ? fromNode
        : findNodeParent({ menu: irdMenu.menu, node: fromNode });

      state.addNodeFrom = fromNode;
      state.nodeParentSelected = parent;
      state.nodeSelected = nodeSelected;
      state.canAddAdditional = !isBeyondMaxChildren(
        fromNode?.children?.length + 1 || 0,
      );
    },
    updateOptionSets(state, action) {
      const { optionTreeKey } = action.payload;
      const { items, nodeSelected, optionSetsDependencies } = current(state);

      const currentOptionsSets = items[optionTreeKey].optionSets;
      const currentAssignedOptionSets = items[optionTreeKey].assignedOptionSets;

      const assignedOptionSets = currentAssignedOptionSets.length
        ? currentAssignedOptionSets
        : nodeSelected.subject.ird_option_sets.map((osdUri) => {
            const foundOsd = optionSetsDependencies.find(
              (osd) => osd.uri === osdUri,
            );

            return {
              id: foundOsd.id,
              title: foundOsd.title,
              uri: foundOsd.uri,
            };
          });

      const unassignedOptionSets = currentOptionsSets.map((optionSet) => ({
        ...optionSet,
        isVisible: assignedOptionSets.every((os) => os.id !== optionSet.id),
      }));

      state.items[optionTreeKey].optionSets = unassignedOptionSets;
      state.items[optionTreeKey].assignedOptionSets = assignedOptionSets;
    },
    addOptionSet(state, action) {
      const { optionTreeKey, optionSet } = action.payload;
      const { items } = current(state);

      const assignedOptionSets = items[optionTreeKey].assignedOptionSets.filter(
        (os) => os.id !== optionSet.id,
      );

      state.items[optionTreeKey].optionSets = items[
        optionTreeKey
      ].optionSets.map((os) => ({
        ...os,
        isVisible: os.id === optionSet.id || os.isVisible,
      }));
      state.items[optionTreeKey].assignedOptionSets = assignedOptionSets;
    },
    removeOptionSet(state, action) {
      const { optionTreeKey, optionSet } = action.payload;
      const { items, optionSetsDependencies } = current(state);

      state.optionSetsDependencies = [
        ...optionSetsDependencies,
        optionSet.subject,
      ];
      state.items[optionTreeKey].optionSets = items[
        optionTreeKey
      ].optionSets.map((o) => ({
        ...o,
        isVisible: o.id !== optionSet.id ? o.isVisible : false,
      }));
    },
    restoreOptionsSet(state, action) {
      const { optionTreeKey } = action.payload;
      const { items } = current(state);
      state.items[optionTreeKey].assignedOptionSets = [];
      state.items[optionTreeKey].optionSets = items[
        optionTreeKey
      ].optionSets.map((o) => ({
        ...o,
        isVisible: true,
      }));
    },
    addItem(state, action) {
      const { optionTreeKey, item } = action.payload;
      const { items } = current(state);

      const assignedItems = items[optionTreeKey].assignedItems.filter(
        (it) => it.id !== item.id,
      );

      state.items[optionTreeKey].items = items[optionTreeKey].items.map(
        (it) => ({
          ...it,
          isVisible: it.id === item.id || it.isVisible,
        }),
      );
      state.items[optionTreeKey].assignedItems = assignedItems;
    },
    removeItem(state, action) {
      const { optionTreeKey, item } = action.payload;
      const { items } = current(state);
      state.items[optionTreeKey].items = items[optionTreeKey].items.map(
        (it) => ({
          ...it,
          isVisible: it.id !== item.id ? it.isVisible : false,
        }),
      );
    },
    setAssignedOptionSets(state, action) {
      const { assignedOptionSets, optionTreeKey } = action.payload;
      state.items[optionTreeKey].assignedOptionSets = assignedOptionSets;
    },
    setAssignedItems(state, action) {
      const { assignedItems, optionTreeKey } = action.payload;
      state.items[optionTreeKey].assignedItems = assignedItems;
    },
    updateIrdItems(state, action) {
      const { optionTreeKey } = action.payload;
      const { items, itemsDependencies, nodeSelected } = current(state);

      const currentIrdItems = items[optionTreeKey].items;
      const currentAssignedIrdItems = items[optionTreeKey].assignedItems;

      const assignedItems = currentAssignedIrdItems.length
        ? currentAssignedIrdItems
        : nodeSelected.subject.ird_items.map((irdUri) => {
            const foundItem = itemsDependencies.find(
              (itd) => itd.uri === irdUri,
            );

            return {
              id: foundItem.id,
              title: foundItem.title,
              uri: foundItem.uri,
            };
          });
      const unassignedItems = currentIrdItems.map((item) => ({
        ...item,
        isVisible: assignedItems.every((aItem) => aItem.id !== item.id),
      }));
      state.items[optionTreeKey].items = unassignedItems;
      state.items[optionTreeKey].assignedItems = assignedItems;
    },
    setLoadingItems(state, action) {
      const { optionTreeKey, loading } = action.payload;
      state.items[optionTreeKey].loading = loading;
    },
    setPublishingStatus(state, action) {
      const { job } = action.payload;
      const incompleteJob = job ? isIncompleteJob(job) : null;
      state.isFailedJob = false;
      state.isIncompleteJob = incompleteJob;
      state.hasUnpublishedChanges = false;
      state.lastPublishJob = job;
      state.lastPublished = job.last_updated;
      state.lastPublishUser = job._edited_by;
      state.publishStatusMessage = getPublishStatusMessage({
        lastPublishJob: job,
        isIncompleteJob: incompleteJob,
        isFailedJob: false,
      });
    },
    setSavingForm(state, action) {
      state.savingForm = action.payload;
    },
    setIrdShowModal(state, action) {
      state.showModal = action.payload;
    },
    setOptionSetDependencies(state, action) {
      const { optionSetsDependencies } = action.payload;
      state.optionSetsDependencies = optionSetsDependencies;
    },
    checkUnpublishedChanges(state) {
      const { irdMenu, lastPublished, productSettings } = current(state);
      const lastPublishedDate = lastPublished ? new Date(lastPublished) : null;
      state.hasUnpublishedChanges = getHasUnpublishedChanges({
        irdMenu,
        lastPublished: lastPublishedDate,
        productSettings,
      });
    },
    cleanIrdMenuSlice() {
      return initialState;
    },
    cleanAssignedItems(state, action) {
      const { optionTreeKey } = action.payload;
      state.items[optionTreeKey].assignedItems = [];
    },
    cleanAssignedOptionSets(state, action) {
      const { optionTreeKey } = action.payload;
      state.items[optionTreeKey].assignedOptionSets = [];
    },
  },
});

export const {
  addItem,
  addOptionSet,
  checkUnpublishedChanges,
  cleanIrdMenuSlice,
  cleanAssignedItems,
  cleanAssignedOptionSets,
  removeItem,
  removeOptionSet,
  restoreOptionsSet,
  setAssignedItems,
  setAssignedOptionSets,
  setCategoryForm,
  setIrdItems,
  setIrdMenu,
  setIrdOptionSets,
  setIrdShowModal,
  setLoadingIrdMenu,
  setLoadingItems,
  setMenu,
  setNodeCategorySelected,
  setNodeCategorySelectedForAdd,
  setNodeItemSelected,
  setNodeItemSelectedForAdd,
  setNodePreviewSelected,
  setNodeSelected,
  setOptionSetDependencies,
  setPage,
  setPublishingStatus,
  setSavingForm,
  updateIrdItems,
  updateJobsStatus,
  updateOptionSets,
} = irdMenuSlice.actions;

export default irdMenuSlice.reducer;
